import { type ResultType } from '../../../../constants/global';
import { type IAgentAssignmentItem } from '../../../../interface/agent-assignment';
import { type IResultAnalyticsData } from '../../../../interface/state/IElectionState';

export type PuStatus = 'uploaded' | 'pending' | 'conflict';
export type StatusFilter = 'all' | PuStatus;
export type SortMode = 'uploadedFirst' | 'code';

/** How the child-entity list is rendered. Card is the default. */
export type ViewMode = 'card' | 'table' | 'kanban' | 'tree';

export const COPY_ALL_SENTINEL = '__all__';

export interface IDerivedStats {
	total: number;
	uploaded: number;
	remaining: number;
	percentage: number;
}

const ISO_SENTINEL_PREFIX = '0001';

/** Derive headline numbers from the API `statistics` block (with safe fallbacks). */
export const derivedStats = (statistics?: { total?: number; uploadedCount?: number }, rowCount = 0): IDerivedStats => {
	const total = statistics?.total ?? rowCount;
	const uploaded = statistics?.uploadedCount ?? 0;
	const remaining = Math.max(total - uploaded, 0);
	const percentage = total > 0 ? Number(Math.min((uploaded / total) * 100, 100).toFixed(1)) : 0;
	return { total, uploaded, remaining, percentage };
};

/** A conflicted result is still "uploaded" but needs attention, so it wins the badge. */
export const puStatus = (item: IResultAnalyticsData): PuStatus => {
	if (item.hasConflict) return 'conflict';
	return item.isUploaded ? 'uploaded' : 'pending';
};

/** Has the row been uploaded by a real result (not the 0001 sentinel)? */
const hasRealUploadDate = (item: IResultAnalyticsData) =>
	!!item.dateUploaded && !item.dateUploaded.startsWith(ISO_SENTINEL_PREFIX);

/** Human-friendly "uploaded on" label, or null when there is no genuine date. */
export const getUploadedLabel = (item: IResultAnalyticsData): string | null => {
	if (!item.isUploaded || !hasRealUploadDate(item)) return null;
	const date = new Date(item.dateUploaded);
	return date.toLocaleDateString(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

export interface IToolbarState {
	search: string;
	statusFilter: StatusFilter;
	sort: SortMode;
}

/** Filter (by code + status) then sort the current page of rows. */
export const applyToolbar = (
	rows: IResultAnalyticsData[],
	{ search, statusFilter, sort }: IToolbarState
): IResultAnalyticsData[] => {
	const term = search.trim().toLowerCase();

	const filtered = rows.filter((row) => {
		const matchesSearch = !term || row.code?.toLowerCase().includes(term);
		const matchesStatus = statusFilter === 'all' || puStatus(row) === statusFilter;
		return matchesSearch && matchesStatus;
	});

	const sorted = [...filtered].sort((a, b) => {
		if (sort === 'code') return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true });
		// uploadedFirst: uploaded → conflict → pending, then by code
		const rank: Record<PuStatus, number> = { uploaded: 0, conflict: 1, pending: 2 };
		const diff = rank[puStatus(a)] - rank[puStatus(b)];
		if (diff !== 0) return diff;
		return (a.code || '').localeCompare(b.code || '', undefined, { numeric: true });
	});

	return sorted;
};

/** Plural label for the child entities under the agent's assignment. Accepts any
 * casing (the deep-drill frames carry the lowercase ec8a/b/c form). */
export const childEntityLabel = (childResultType?: string): string => {
	switch (childResultType?.toUpperCase()) {
		case 'EC8A':
			return 'Polling Units';
		case 'EC8B':
			return 'Wards';
		case 'EC8C':
			return 'LGAs';
		case 'EC8D':
			return 'States';
		default:
			return 'Results';
	}
};

// ────────────────────────────────────────────────────────────
// Hierarchy drill-down (grandchildren and deeper)
// ────────────────────────────────────────────────────────────

/** Scope for querying a row's own children (one rung below the row's level). */
export interface IChildScope {
	// Child level to enumerate, lowercase — the value the ResultAnalytics endpoint expects.
	resultType: Lowercase<ResultType>;
	// The numeric id of the row's OWN entity, used to scope the child query.
	scope: { stateId?: number; lgaId?: number; wardId?: number };
}

/** A polling-unit row is a leaf — it has no children to drill into. */
export const isLeafRow = (row: IResultAnalyticsData): boolean => row.resultType?.toUpperCase() === 'EC8A';

/**
 * Given an analytics row, describe how to fetch its children (the next rung down),
 * scoped by the row's own numeric id. Returns null for polling-unit (leaf) rows.
 * State(ec8d)→LGA(ec8c) by stateId, LGA(ec8c)→Ward(ec8b) by lgaId, Ward(ec8b)→PU(ec8a) by wardId.
 */
export const childScopeOf = (row: IResultAnalyticsData): IChildScope | null => {
	switch (row.resultType?.toUpperCase()) {
		case 'EC8D':
			return { resultType: 'ec8c', scope: { stateId: row.stateId } };
		case 'EC8C':
			return { resultType: 'ec8b', scope: { lgaId: row.lgaId } };
		case 'EC8B':
			return { resultType: 'ec8a', scope: { wardId: row.wardId } };
		default:
			return null; // ec8a (polling unit) = leaf
	}
};

/** Singular label for one child entity (drawer header / aria copy). Accepts any casing. */
export const childEntityLabelSingular = (childResultType?: string): string => {
	switch (childResultType?.toUpperCase()) {
		case 'EC8A':
			return 'Polling Unit';
		case 'EC8B':
			return 'Ward';
		case 'EC8C':
			return 'LGA';
		case 'EC8D':
			return 'State';
		default:
			return 'Entity';
	}
};

/** True when an ISO date string is genuine (present and not the 0001 sentinel). */
export const hasRealIsoDate = (iso?: string | null): boolean => !!iso && !iso.startsWith(ISO_SENTINEL_PREFIX);

// ────────────────────────────────────────────────────────────
// Agent coverage (derived from the AgentAssignment/by-entity endpoint)
// ────────────────────────────────────────────────────────────

/**
 * Assignment/coverage state for a child entity. This is the *assignment*
 * endpoint's view (who covers the entity + whether they've uploaded) and is
 * distinct from `PuStatus`, which is derived from the result-analytics row.
 */
export type CoverageState = 'unassigned' | 'agentUnavailable' | 'conflict' | 'uploaded' | 'pending';

export interface RowCoverage {
	state: CoverageState;
	agentName: string | null;
	isActive: boolean;
	/** The assigned agent's core user id — the chat counterpart id. Null when nobody is assigned. */
	agentId: number | null;
}

/** Best display name for an assigned agent, or null when unavailable. */
export const assignedAgentName = (item: IAgentAssignmentItem): string | null => {
	const agent = item.agent;
	if (!agent) return null;
	const full = `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim();
	return full || agent.userName || null;
};

/** Collapse an assignment item into a single coverage state for badges. */
export const deriveCoverage = (item: IAgentAssignmentItem): RowCoverage => {
	const name = assignedAgentName(item);
	const isActive = !!item.agent?.isActive;
	const agentId = item.agent?.id ?? null;

	if (!item.isAssigned) return { state: 'unassigned', agentName: null, isActive: false, agentId: null };
	if (item.assignmentMeta?.agentUnavailable) return { state: 'agentUnavailable', agentName: name, isActive, agentId };
	if (item.assignmentMeta?.hasConflict) return { state: 'conflict', agentName: name, isActive, agentId };
	if (item.assignmentMeta?.isUploaded && hasRealIsoDate(item.assignmentMeta?.dateUploaded))
		return { state: 'uploaded', agentName: name, isActive, agentId };
	return { state: 'pending', agentName: name, isActive, agentId };
};
